import { AreaValue, AreaValueDataObject } from 'ComponentLibrary/src/common/area'
import { forwardRef } from 'react'
import { Control, FieldPathByValue, FieldPathValue, FieldValues, UseControllerProps, useController } from 'react-hook-form'
import { NumberInput, NumberInputProps } from '../NumberInput/NumberInput'
import { isRequiredRule } from '../TextInput/TextInput'

export type AreaInputProps = Omit<NumberInputProps, 'defaultValue' | 'format' | 'onValueChange' | 'value'> & {
    defaultValue?: AreaValueDataObject
    onValueChange?: (value: AreaValue | undefined) => void
    value?: AreaValueDataObject
}

export const AreaInput = forwardRef<HTMLInputElement, AreaInputProps>(function AreaInput(props, ref) {
    const { defaultValue, onValueChange, unit = 'm²', value, ...numberInputProps } = props
    const isControlled = Object.prototype.hasOwnProperty.call(props, 'value')
    const numberValueProps = isControlled ? { value: value?.value } : {}

    return (
        <NumberInput
            {...numberInputProps}
            {...numberValueProps}
            defaultValue={defaultValue?.value}
            onValueChange={(nextValue) => {
                onValueChange?.(nextValue === undefined ? undefined : new AreaValue(nextValue))
            }}
            ref={ref}
            unit={unit}
        />
    )
})

type AreaFieldPath<TFieldValues extends FieldValues> = FieldPathByValue<TFieldValues, AreaValueDataObject | undefined>

type FormAreaInputRules<TFieldValues extends FieldValues, TName extends AreaFieldPath<TFieldValues>> = Omit<
    NonNullable<UseControllerProps<TFieldValues, TName>['rules']>,
    'max' | 'maxLength' | 'min' | 'minLength' | 'pattern' | 'valueAsNumber'
>

export type FormAreaInputProps<
    TFieldValues extends FieldValues,
    TName extends AreaFieldPath<TFieldValues> = AreaFieldPath<TFieldValues>,
> = Omit<AreaInputProps, 'defaultValue' | 'errorMessage' | 'name' | 'onValueChange' | 'required' | 'value'> & {
    control: Control<TFieldValues>
    defaultValue?: UseControllerProps<TFieldValues, TName>['defaultValue']
    name: TName
    rules?: FormAreaInputRules<TFieldValues, TName>
}

export function FormAreaInput<TFieldValues extends FieldValues, TName extends AreaFieldPath<TFieldValues> = AreaFieldPath<TFieldValues>>({
    control,
    defaultValue,
    name,
    onBlur,
    rules,
    ...areaInputProps
}: FormAreaInputProps<TFieldValues, TName>) {
    const { validate, ...otherRules } = rules ?? {}
    const { field, fieldState } = useController({
        control,
        defaultValue,
        name,
        rules: {
            ...otherRules,
            validate:
                validate ??
                ((fieldValue: FieldPathValue<TFieldValues, TName>) =>
                    fieldValue === undefined || isAreaValueDataObject(fieldValue) || 'Bitte geben Sie eine Zahl ein'),
        },
    })

    return (
        <AreaInput
            {...areaInputProps}
            errorMessage={fieldState.error ? (fieldState.error.message?.toString() ?? '') : undefined}
            name={field.name}
            onBlur={(event) => {
                field.onBlur()
                onBlur?.(event)
            }}
            onValueChange={field.onChange}
            ref={field.ref}
            required={isRequiredRule(rules?.required)}
            value={field.value}
        />
    )
}

function isAreaValueDataObject(value: unknown): value is AreaValueDataObject {
    if (typeof value !== 'object' || value === null) {
        return false
    }

    const areaValue = value as Partial<AreaValueDataObject>
    return (
        typeof areaValue.value === 'number' &&
        !Number.isNaN(areaValue.value) &&
        (areaValue.unit === 'sqm' || areaValue.unit === 'living_space')
    )
}
